// pages/Campaigns/Gmail/ListGmailEmails.js
import React, { useState, useEffect, useCallback, useMemo } from "react"; // 'useMemo' is now imported
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import { useDebounce } from 'use-debounce';
import GmailReplyWizard from "./GmailReplyWizard";

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal as MuiModal,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import ReplyModal from "./ReplyModal";
import PromptSettings from "./PromptSettings";


export default function ListGmailEmails({ refreshTrigger, chatbotId, permission }) {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isTraining, setIsTraining] = useState(false);
    
    // --- FILTER STATE ---
    const [trainingStatusFilter, setTrainingStatusFilter] = useState('all');
    const [emailStatusFilter, setEmailStatusFilter] = useState('all');
    // FIX 2: Set the default time range filter to 'last_day' (24 hours).
    const [timeRangeFilter, setTimeRangeFilter] = useState('last_day');
    const [subjectSearch, setSubjectSearch] = useState('');
    const [emailSearch, setEmailSearch] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Debounce search inputs
    const [debouncedSubject] = useDebounce(subjectSearch, 500);
    const [debouncedEmail] = useDebounce(emailSearch, 500);

    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [selectedEmailsForWizard, setSelectedEmailsForWizard] = useState([]);

    // FIX 1: useMemo stabilizes the 'params' object, preventing it from being recreated
    // on every render. This stops the infinite loop.
    const params = useMemo(() => (chatbotId ? { chatbot_id: chatbotId } : {}), [chatbotId]);

    const fetchEmails = useCallback(async (isManualFetch = false) => {
        setLoading(true);
        setError(null);
        if (isManualFetch) {
            setInfoMessage('');
        }
        try {
            const apiParams = {
                ...params,
                subject: debouncedSubject,
                email: debouncedEmail,
                training_status: trainingStatusFilter,
                email_status: emailStatusFilter,
                time_range: timeRangeFilter,
                start_date: startDate ? startDate.toISOString() : undefined,
                end_date: endDate ? endDate.toISOString() : undefined,
            };

            const response = await axiosInstance.get(`/api/gmail/check-emails`, {
                headers: isManualFetch ? { 'X-Manual-Fetch': 'true' } : {},
                params: apiParams
            });

            setEmails(response.data.data || []);
            if (isManualFetch && response.data.message) {
                setInfoMessage(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Error fetching emails.");
            setInfoMessage('');
        } finally {
            setLoading(false);
        }
    }, [
        params, debouncedSubject, debouncedEmail, trainingStatusFilter,
        emailStatusFilter, timeRangeFilter, startDate, endDate
    ]);

    useEffect(() => {
        fetchEmails(false);
    }, [fetchEmails, refreshTrigger]);

    const handleManualFetch = () => fetchEmails(true);

    const handleOpenReplyModal = useCallback((email) => {
        setSelectedEmail(email);
        setIsReplyModalOpen(true);
    }, []);

    const handleCloseReplyModal = useCallback((shouldRefresh = false) => {
        setIsReplyModalOpen(false);
        setSelectedEmail(null);
        if (shouldRefresh) {
            fetchEmails(true);
        }
    }, [fetchEmails]);

    const handleTrainEmail = useCallback(async (email) => {
        setIsTraining(true);
        setInfoMessage(`Training AI with email: ${email.subject}...`);
        setError('');
        try {
            const response = await axiosInstance.post(`/api/gmail/train-reply`,
                { message_db_id: email.id },
                { params }
            );
            setInfoMessage(response.data.message);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to train email.');
        } finally {
            setIsTraining(false);
        }
    }, [fetchEmails, params]);

    const handleUntrainEmail = useCallback(async (email) => {
        setIsTraining(true);
        setInfoMessage(`Removing training for email: ${email.subject}...`);
        setError('');
        try {
            const response = await axiosInstance.post(`/api/gmail/untrain-reply`,
                { message_db_id: email.id },
                { params }
            );
            setInfoMessage(response.data.message);
            await fetchEmails(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to untrain email.');
        } finally {
            setIsTraining(false);
        }
    }, [fetchEmails, params]);

    const handleClearFilters = () => {
        setTimeRangeFilter('all');
        setSubjectSearch('');
        setEmailSearch('');
        setStartDate(null);
        setEndDate(null);
        setTrainingStatusFilter('all');
        setEmailStatusFilter('all');
    };
    
    const columns = useMemo(() => [
        { name: "From", selector: (row) => row.from_address, sortable: true, grow: 2, wrap: true },
        { name: "Subject", selector: (row) => row.subject, sortable: true, grow: 3, wrap: true },
        { name: "Received", selector: (row) => new Date(row.received_at).toLocaleString(), sortable: true, grow: 2 },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => {
                let badgeClass = 'badge ';
                let text = row.status.replace(/_/g, ' ').toUpperCase();

                if (row.is_trained) {
                    badgeClass += 'bg-primary';
                    text += ' (TRAINED)';
                } else {
                    switch (row.status) {
                        case 'new': badgeClass += 'bg-info'; break;
                        case 'replied_manual': case 'replied_ai': badgeClass += 'bg-success'; break;
                        case 'reply_generated': badgeClass += 'bg-warning'; break;
                        case 'processing': badgeClass += 'bg-secondary'; break;
                        case 'error': badgeClass += 'bg-danger'; break;
                        default: badgeClass += 'bg-secondary';
                    }
                }
                return <span className={badgeClass}>{text}</span>;
            }
        },
        {
            name: "Actions",
            cell: (row) => {
                const isReplied = row.status === 'replied_manual' || row.status === 'replied_ai';
                const commonDisabled = loading || isTraining;

                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Email & Reply" arrow>
                            <span>
                                <IconButton size="small" color="info" onClick={() => handleOpenReplyModal(row)} disabled={commonDisabled}>
                                    <i className="fa fa-eye"></i>
                                </IconButton>
                            </span>
                        </Tooltip>
                        {isReplied && (
                            row.is_trained ? (
                                <Tooltip title="Remove this reply from AI's knowledge" arrow>
                                    <span>
                                        <IconButton size="small" color="error" onClick={() => handleUntrainEmail(row)} disabled={commonDisabled}>
                                            <i className="fa fa-unlink"></i>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Train AI with this reply" arrow>
                                    <span>
                                        <IconButton size="small" color="primary" onClick={() => handleTrainEmail(row)} disabled={commonDisabled}>
                                            <i className="fa fa-magic"></i>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        )}
                    </Stack>
                )
            },
        },
    ], [loading, isTraining, handleOpenReplyModal, handleTrainEmail, handleUntrainEmail]);

    const selectableRowDisabled = row => {
        // Only allow selection of emails that haven't been replied to yet
        const isSelectable = row.status === 'new' || row.status === 'reply_generated' || row.status === 'error';
        return !isSelectable;
    };

    const settingsModalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '80%', md: '60vw' }, maxWidth: '800px',
        maxHeight: '90vh', bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: 3,
        display: 'flex', flexDirection: 'column', outline: 'none',
    };

    // Handler for opening wizard
    const handleOpenWizard = () => {
        if (selectedRows.length === 0) {
            setError('Please select at least one email to process.');
            return;
        }
        setSelectedEmailsForWizard(selectedRows);
        setIsWizardOpen(true);
    };

    const handleCloseWizard = () => {
        setIsWizardOpen(false);
        setSelectedEmailsForWizard([]);
    };

    const handleWizardSuccess = () => {
        setIsWizardOpen(false);
        setSelectedEmailsForWizard([]);
        setSelectedRows([]);
        fetchEmails(true);
    };


    return (
        <div>
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => setIsSettingsModalOpen(true)} disabled={loading}>
                    <i className="fa fa-cog me-1"></i> Settings
                </Button>
                
                <Button variant="contained" color="secondary" onClick={handleManualFetch} disabled={loading}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : <i className="fa fa-refresh me-1"></i>}
                    Check for New Emails
                </Button>

                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleOpenWizard}
                    disabled={selectedRows.length === 0 || loading}
                >
                    <i className="fa fa-magic me-1"></i>
                    Process Selected ({selectedRows.length})
                </Button>
            </Stack>

            <Card sx={{ mb: 3, overflow: 'visible' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md>
                            <Typography variant="h6">
                                Filters
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md="auto">
                            <Button variant="outlined" onClick={handleClearFilters} size="small">
                                Clear Filters
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Search by Subject" variant="outlined" value={subjectSearch} onChange={(e) => setSubjectSearch(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Search by Email" variant="outlined" value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Training Status</InputLabel>
                                <Select value={trainingStatusFilter} onChange={(e) => setTrainingStatusFilter(e.target.value)} label="Training Status">
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="trained">Trained</MenuItem>
                                    <MenuItem value="untrained">Untrained</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Email Status</InputLabel>
                                <Select value={emailStatusFilter} onChange={(e) => setEmailStatusFilter(e.target.value)} label="Email Status">
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="new">New</MenuItem>
                                    <MenuItem value="replied_ai">Replied (AI)</MenuItem>
                                    <MenuItem value="replied_manual">Replied (Manual)</MenuItem>
                                    <MenuItem value="reply_generated">Reply Generated</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                    <MenuItem value="error">Error</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Time Range</InputLabel>
                                <Select value={timeRangeFilter} onChange={(e) => setTimeRangeFilter(e.target.value)} label="Time Range">
                                    <MenuItem value="all">All Time</MenuItem>
                                    <MenuItem value="last_day">Last 24 Hours</MenuItem>
                                    <MenuItem value="last_week">Last 7 Days</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth size="small" variant="outlined" />} />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} md={6}>
                             <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth size="small" variant="outlined" />} />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {error && <div className="alert alert-danger mt-2">{error}</div>}
            {infoMessage && <div className="alert alert-info mt-2">{infoMessage}</div>}

            <DataTable
                columns={columns}
                data={emails}
                progressPending={loading}
                pagination
                responsive
                highlightOnHover
                selectableRows
                onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                selectableRowDisabled={selectableRowDisabled}
                clearSelectedRows={isWizardOpen} // Clear selection when wizard is managed
            />
            {isReplyModalOpen && (
                <ReplyModal
                    isOpen={isReplyModalOpen}
                    onClose={handleCloseReplyModal}
                    emailData={selectedEmail}
                    onTrain={handleTrainEmail}
                    onUntrain={handleUntrainEmail}
                    chatbotId={chatbotId}
                    permission={permission}
                />
            )}
            <MuiModal open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)}>
                <Box sx={settingsModalStyle}>
                    <h4 className="mb-3">AI Prompt Settings</h4>
                    <PromptSettings chatbotId={chatbotId} permission={permission} />
                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="outlined" onClick={() => setIsSettingsModalOpen(false)}>Close</Button>
                    </div>
                </Box>
            </MuiModal>
            {/* // Add the wizard component at the end, before closing div */}
            {isWizardOpen && (
                <GmailReplyWizard
                    isOpen={isWizardOpen}
                    onClose={handleCloseWizard}
                    selectedEmails={selectedEmailsForWizard}
                    chatbotId={chatbotId}
                    onSuccess={handleWizardSuccess}
                />
            )}
        </div>
    );
}